import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Tag, Typography
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { investmentsApi } from './investmentsApi';
import {
  INVESTMENT_CLASSES, STATUS_COLOR, STATUS_LABEL,
  findInvestmentFormErrors, friendlyErrorFor, formatRupee
} from './investmentsHelpers';
import '../accounts.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Add / Edit / View modal (Stage 6B.9E).
//
// Centered 1080-wide AntD Modal. Mirrors the Fixed Asset modal shape.
// ─────────────────────────────────────────────────────────────────

const STATUS_VIEW_MODES = new Set(['view']);

function LedgerSelect({ value, onChange, disabled, subtype, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetcher = useCallback(async (q) => {
    setLoading(true);
    try {
      if (subtype) {
        return await investmentsApi.ledgersBySubtype(subtype, { q, limit: 100 });
      }
      return await investmentsApi.ledgerLookup(q || '', 100);
    } finally { setLoading(false); }
  }, [subtype]);

  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    try { setOptions(await fetcher('')); } catch (_e) { /* swallow */ }
  }, [options.length, fetcher]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setOptions(await fetcher(text || '')); } catch (_e) { /* noop */ }
    }, 200);
  }, [fetcher]);

  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      allowClear
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: (
          <span>
            <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
            {o.accountName}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>
              · {o.accountType}
            </span>
          </span>
        )
      }))}
    />
  );
}

function defaultsFor(mode, record) {
  if (mode === 'add' || !record) {
    return {
      investmentDate:   dayjs(),
      principalAmount:  0,
      currentValue:     0
    };
  }
  return {
    investmentName:                record.investmentName,
    categoryId:                    record.categoryId,
    investmentLedgerAccountId:     record.investmentLedgerAccountId,
    incomeLedgerAccountId:         record.incomeLedgerAccountId,
    gainLossLedgerAccountId:       record.gainLossLedgerAccountId,
    institutionName:               record.institutionName,
    brokerName:                    record.brokerName,
    folioNo:                       record.folioNo,
    certificateNo:                 record.certificateNo,
    referenceNo:                   record.referenceNo,
    investmentDate:                record.investmentDate ? dayjs(record.investmentDate) : null,
    maturityDate:                  record.maturityDate ? dayjs(record.maturityDate) : null,
    principalAmount:               Number(record.principalAmount) || 0,
    currentValue:                  Number(record.currentValue)   || 0,
    units:                         record.units,
    interestRate:                  record.interestRate,
    remarks:                       record.remarks
  };
}

export default function InvestmentModal({
  open, mode, record, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const isView   = STATUS_VIEW_MODES.has(mode);
  const readOnly = isView;
  const status   = record ? record.status : null;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(defaultsFor(mode, record));
    investmentsApi.listCategories({ page: 1, pageSize: 200,
      filters: [{ field: 'is_active', op: 'eq', value: 'true' }] })
      .then((out) => setCategoryOptions(((out && out.rows) || [])))
      .catch(() => setCategoryOptions([]));
  }, [open, mode, record, form]);

  const saveInvestment = useCallback(async () => {
    try {
      const headerValues = await form.validateFields();
      const payload = {
        ...headerValues,
        investmentDate: headerValues.investmentDate
          ? headerValues.investmentDate.format('YYYY-MM-DD') : null,
        maturityDate:   headerValues.maturityDate
          ? headerValues.maturityDate.format('YYYY-MM-DD')  : null
      };
      const errs = findInvestmentFormErrors(payload);
      if (errs.length) {
        Modal.warning({
          title: 'Please fix these issues',
          content: <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}>
            {errs.join('\n')}
          </pre>,
          okText: 'Close'
        });
        return;
      }
      setBusy(true);
      let saved;
      if (mode === 'add') {
        saved = await investmentsApi.create(payload);
        message.success(`Investment ${saved.investmentCode} created.`);
      } else if (mode === 'edit') {
        saved = await investmentsApi.update(record.id, payload);
        message.success('Investment updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [form, mode, record, onSaved]);

  const title = useMemo(() => {
    if (mode === 'add') return 'New Investment';
    const code = record && record.investmentCode ? ` — ${record.investmentCode}` : '';
    if (mode === 'view') return `View Investment${code}`;
    return `Edit Investment${code}`;
  }, [mode, record]);

  const footer = (
    <Space>
      {!readOnly ? (
        <Button type="primary" icon={<SaveOutlined />} loading={busy} onClick={saveInvestment}>
          {mode === 'add' ? 'Create Investment' : 'Save Changes'}
        </Button>
      ) : null}
      <Button onClick={onClose}>Close</Button>
    </Space>
  );

  return (
    <Modal
      title={
        <span>
          {title}
          {status ? (
            <Tag color={STATUS_COLOR[status] || 'default'} style={{ marginInlineStart: 12 }}>
              {STATUS_LABEL[status] || status}
            </Tag>
          ) : null}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form">
        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Investment Code">
              <Input value={(record && record.investmentCode) || (mode === 'add' ? '(auto-generated)' : '')} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Investment Name" name="investmentName"
              rules={[{ required: true, message: 'Investment name is required.' }]}>
              <Input maxLength={200} disabled={readOnly} placeholder="e.g. Axis Bank FD 1 yr · 7.25%" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Category" name="categoryId">
              <Select allowClear disabled={readOnly} placeholder="Select category"
                options={categoryOptions.map((c) => ({
                  value: c.id,
                  label: `${c.categoryCode} · ${c.categoryName} (${c.investmentClass})`
                }))} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Investment Ledger" name="investmentLedgerAccountId"
              rules={[{ required: true, message: 'Select an Investment ledger.' }]}>
              <LedgerSelect subtype="investment" disabled={readOnly}
                placeholder="Search Investment ledgers…" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Income Ledger" name="incomeLedgerAccountId">
              <LedgerSelect disabled={readOnly}
                placeholder="Interest / dividend / income ledger" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Gain / Loss Ledger" name="gainLossLedgerAccountId">
              <LedgerSelect disabled={readOnly}
                placeholder="Gain on investment / Loss on investment" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Institution" name="institutionName">
              <Input maxLength={200} disabled={readOnly} placeholder="HDFC Bank, SBI MF, etc." />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Broker" name="brokerName">
              <Input maxLength={200} disabled={readOnly} placeholder="Zerodha, ICICI Direct, …" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Folio No" name="folioNo">
              <Input maxLength={100} disabled={readOnly} placeholder="MF folio #" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Certificate No" name="certificateNo">
              <Input maxLength={100} disabled={readOnly} placeholder="FD / bond certificate #" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Investment Date" name="investmentDate"
              rules={[{ required: true, message: 'Investment date is required.' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Maturity Date" name="maturityDate">
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Reference No" name="referenceNo">
              <Input maxLength={100} disabled={readOnly} placeholder="Internal reference" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Interest Rate (%)" name="interestRate">
              <InputNumber min={0} max={100} step={0.05} style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Principal Amount (₹)" name="principalAmount"
              rules={[{ required: true, message: 'Principal amount is required.' }]}>
              <InputNumber min={0} step={1000} style={{ width: '100%' }} disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Current Value (₹)" name="currentValue">
              <InputNumber min={0} step={1000} style={{ width: '100%' }} disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Units" name="units">
              <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Accrued Income (₹)">
              <Input disabled value={record
                ? Number(record.accruedIncome || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '0.00'} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={2000} disabled={readOnly}
                placeholder="Optional notes — tenor, lock-in, premature withdrawal terms, etc." />
            </Form.Item>
          </Col>
        </Row>

        {record ? (
          <Alert type="info" showIcon style={{ marginTop: 6 }}
            message="Investment summary"
            description={
              <span>
                Principal <Text strong>₹ {formatRupee(record.principalAmount)}</Text>
                {' · '}
                Current value <Text strong>₹ {formatRupee(record.currentValue)}</Text>
                {' · '}
                Accrued income <Text strong>₹ {formatRupee(record.accruedIncome)}</Text>
              </span>
            } />
        ) : null}
      </Form>
    </Modal>
  );
}
